"use client";

import { gtmSendEvent } from "@wojo/analytics";
import { StoryblokImage } from "@wojo/storyblok";
import { Button, Card, Text } from "@wojo/ui";
import { truncate } from "lodash";
import { useEffect, useRef, useState } from "react";
import { FiMinus, FiPlus } from "react-icons/fi";
import { TourAccommodationContent } from "../../../generated/graphql";

type AccommodationCardProps = {
    accommodation: TourAccommodationContent;
};

// 280 is the max width of mobile image, multiple by 2 for high density screens
const IMAGE_WIDTH_MOBILE = 280 * 2;

export const AccommodationCard: React.FC<AccommodationCardProps> = ({
    accommodation,
}) => {
    const isShortDescription = (accommodation.description ?? "").length <= 230;
    const [isExpanded, setIsExpanded] = useState(isShortDescription);
    const revealedElRef = useRef<HTMLDivElement>(null);
    const toggleElRef = useRef<HTMLButtonElement>(null);
    const description = isExpanded
        ? accommodation.description
        : truncate(accommodation.description ?? "", {
              length: 230,
              separator: /,? +/,
          });

    useEffect(() => {
        if (isExpanded) {
            revealedElRef.current?.focus();
        }
    }, [isExpanded]);

    return (
        <Card
            variant="standard"
            key={accommodation.name}
            style={{ padding: 0, overflow: "hidden" }}
        >
            {accommodation.image?.filename && (
                <StoryblokImage
                    style={{
                        width: "100%",
                        aspectRatio: "3/2",
                        display: "flex",
                    }}
                    mobile={{
                        asset: accommodation.image,
                        size: {
                            width: IMAGE_WIDTH_MOBILE,
                        },
                    }}
                />
            )}
            <div style={{ padding: "var(--g-spacing-md)" }}>
                <Text.Heading
                    order="5"
                    tag="h3"
                    style={{ paddingBottom: "var(--g-spacing-xs)" }}
                >
                    {accommodation.name}
                </Text.Heading>
                <div
                    tabIndex={-1}
                    ref={
                        isExpanded && !isShortDescription ? revealedElRef : null
                    }
                >
                    <Text.Body>{description}</Text.Body>
                </div>

                {!isShortDescription && (
                    <Button
                        variant="quiet"
                        onClick={() => {
                            gtmSendEvent({
                                event: "track",
                                ga4Tracking: {
                                    ga4_action: !isExpanded
                                        ? "EXPANDED"
                                        : "COLLAPSED",
                                    ga4_object: "ELEMENT",
                                    elementType: "readMore",
                                    elementState: !isExpanded
                                        ? "expanded"
                                        : "collapsed",
                                    elementID: "readmoreHotels",
                                },
                            });
                            setIsExpanded(!isExpanded);
                        }}
                        style={{ marginTop: "var(--g-spacing-sm)" }}
                        adornRight={
                            isExpanded ? (
                                <FiMinus aria-hidden />
                            ) : (
                                <FiPlus aria-hidden />
                            )
                        }
                        aria-expanded={isExpanded}
                        ref={toggleElRef}
                    >
                        Read {isExpanded ? "less" : "more"}
                    </Button>
                )}
            </div>
        </Card>
    );
};
